import { RotateCcw, Sparkles, Volume2, Waves } from "lucide-react";

import { VoicePicker } from "@/components/room/VoicePicker";
import { Button } from "@/components/ui/Button";
import { SelectField } from "@/components/ui/Field";
import { Slider } from "@/components/ui/Slider";
import { Toggle } from "@/components/ui/Toggle";
import { tone, type Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";
import { gateThreshold, meterLevel } from "@/lib/audio/MicProcessor";
import {
  MAX_GAIN,
  MIN_GAIN,
  supportedMicControls,
  type MicQuality,
} from "@/lib/audio/micPreferences";
import { useMicLevel } from "@/lib/audio/useMicLevel";
import { useMicPreferences } from "@/lib/audio/useMicPreferences";
import { useT, type MessageKey } from "@/lib/i18n";

const QUALITIES: { id: MicQuality; label: MessageKey; body: MessageKey }[] = [
  { id: "speech", label: "mic.qualitySpeech", body: "mic.qualitySpeechBody" },
  { id: "clear", label: "mic.qualityClear", body: "mic.qualityClearBody" },
  { id: "music", label: "mic.qualityMusic", body: "mic.qualityMusicBody" },
];

const NO_TRACK = () => null;

export function MicSettings({
  tone: which = "stage",
  micTrack,
  onApply,
  monitor,
}: {
  tone?: Tone | undefined;

  micTrack?: (() => MediaStreamTrack | null) | undefined;

  onApply?: ((options: { renegotiate: boolean }) => void) | undefined;

  monitor?: ((on: boolean) => boolean) | undefined;
}) {
  const { preferences, set, reset } = useMicPreferences();
  const styles = tone(which);
  const t = useT();
  const supported = supportedMicControls();
  const { level, listening } = useMicLevel(micTrack ?? NO_TRACK, micTrack !== undefined);

  const apply = <K extends keyof typeof preferences>(key: K, value: (typeof preferences)[K]) => {
    set(key, value);
    onApply?.({ renegotiate: key === "quality" });
  };

  const threshold = preferences.gate === 0 ? null : meterLevel(gateThreshold(preferences.gate));
  const gated = threshold !== null && level < threshold;

  return (
    <div className="flex flex-col gap-3">
      {micTrack !== undefined ? (
        <div className="flex flex-col gap-1.5">
          <span className={cn("text-sm font-medium", styles.text)}>{t("mic.level")}</span>
          <div
            className={cn(
              "relative h-3 w-full overflow-hidden rounded-full",
              styles.switchOff,
              !listening && "opacity-50",
            )}
          >
            <div
              className={cn(
                "h-full rounded-full transition-[width] duration-75",
                gated ? "bg-brand/35" : "bg-brand",
              )}
              style={{ width: `${Math.round(level * 100)}%` }}
            />
            {threshold !== null ? (
              <span
                aria-hidden
                className="absolute inset-y-0 w-0.5 bg-warning"
                style={{ insetInlineStart: `${Math.round(threshold * 100)}%` }}
              />
            ) : null}
          </div>
          <p className={cn("text-xs", styles.muted)}>
            {listening ? t("mic.levelBody") : t("mic.levelOff")}
          </p>
        </div>
      ) : null}

      <div className={cn("flex flex-col gap-3 border-t pt-3", styles.line)}>
        <VoicePicker
          monitor={monitor}
          onApply={() => {
            onApply?.({ renegotiate: false });
          }}
          tone={which}
        />
      </div>

      {supported.noiseSuppression ? (
        <Toggle
          checked={preferences.noiseSuppression}
          description={t("mic.noiseSuppressionBody")}
          label={t("mic.noiseSuppression")}
          onChange={(next) => {
            apply("noiseSuppression", next);
          }}
          tone={which}
        />
      ) : null}

      {supported.echoCancellation ? (
        <Toggle
          checked={preferences.echoCancellation}
          description={t("mic.echoCancellationBody")}
          label={t("mic.echoCancellation")}
          onChange={(next) => {
            apply("echoCancellation", next);
          }}
          tone={which}
        />
      ) : null}

      {supported.autoGain ? (
        <Toggle
          checked={preferences.autoGain}
          description={t("mic.autoGainBody")}
          label={t("mic.autoGain")}
          onChange={(next) => {
            apply("autoGain", next);
          }}
          tone={which}
        />
      ) : null}

      {!supported.noiseSuppression && !supported.echoCancellation && !supported.autoGain ? (
        <p className={cn("text-xs", styles.muted)}>{t("mic.noBrowserProcessing")}</p>
      ) : null}

      <SelectField
        hint={t(QUALITIES.find((entry) => entry.id === preferences.quality)?.body ?? "mic.quality")}
        label={t("mic.quality")}
        onChange={(value) => {
          apply("quality", value as MicQuality);
        }}
        options={QUALITIES.map((entry) => ({ value: entry.id, label: t(entry.label) }))}
        tone={which}
        value={preferences.quality}
      />

      <Slider
        icon={<Volume2 aria-hidden className={cn("h-4 w-4 shrink-0", styles.muted)} />}
        label={t("mic.volume")}
        max={Math.round(MAX_GAIN * 100)}
        maxLabel={t("mic.volumeMax")}
        min={Math.round(MIN_GAIN * 100)}
        minLabel={t("mic.volumeMin")}
        onChange={(value) => {
          apply("gain", value / 100);
        }}
        readout={t("mic.percent", { value: Math.round(preferences.gain * 100) })}
        step={5}
        tone={which}
        value={Math.round(preferences.gain * 100)}
      />

      <Slider
        icon={<Sparkles aria-hidden className={cn("h-4 w-4 shrink-0", styles.muted)} />}
        label={t("mic.clarity")}
        max={100}
        maxLabel={t("mic.clarityMax")}
        min={0}
        minLabel={t("mic.clarityMin")}
        onChange={(value) => {
          apply("clarity", value / 100);
        }}
        readout={
          preferences.clarity === 0
            ? t("mic.clarityMin")
            : t("mic.percent", { value: Math.round(preferences.clarity * 100) })
        }
        step={5}
        tone={which}
        value={Math.round(preferences.clarity * 100)}
      />

      <Slider
        icon={<Waves aria-hidden className={cn("h-4 w-4 shrink-0", styles.muted)} />}
        label={t("mic.gate")}
        max={100}
        maxLabel={t("mic.gateMax")}
        min={0}
        minLabel={t("mic.gateOff")}
        onChange={(value) => {
          apply("gate", value / 100);
        }}
        readout={
          preferences.gate === 0
            ? t("mic.gateOff")
            : t("mic.percent", { value: Math.round(preferences.gate * 100) })
        }
        step={5}
        tone={which}
        value={Math.round(preferences.gate * 100)}
      />

      <p className={cn("text-xs", styles.muted)}>{t("mic.clarityBody")}</p>
      <p className={cn("text-xs", styles.muted)}>{t("mic.gateBody")}</p>

      <div>
        <Button
          onClick={() => {
            reset();
            onApply?.({ renegotiate: true });
          }}
          size="sm"
          tone={which}
          variant="secondary"
        >
          <RotateCcw aria-hidden className="h-3.5 w-3.5" />
          {t("mic.reset")}
        </Button>
      </div>
    </div>
  );
}
